import { createPagination } from "./utilities/paginationBuilder";
import { getFilterClause, createPartialURL } from "./utilities/urlBuilder";
import { Deserializer } from "jsonapi-serializer";
import { Lift, LiftWrapper } from "../models/Lift";
import { ListQuery } from "../models/ListQuery";
import { APIConnector } from './api/APIConnector';
import { genericError } from "../models/GenericError";
import { processServerError, getConstraintViolation } from "./utilities/ServiceError";
import { Name } from "../models/Name";
import { PaginationModel } from "../models/Pagination";
import { Reader, ReaderWrapper } from "../models/Reader";
import { getDeviceLicenceStatus } from "./utilities/StatusBuilder";
import { INTERNAL_SERVER_ERROR } from "../constants/response-status-codes";
import { IOModule, IOModuleWrapper } from "../models/IOModule";
import { StoreEntityResult } from "../models/services/StoreEntityResult";
import { DeleteEntityResult } from "../models/services/DeleteEntityResult";
import { MapRelay } from "../models/MapRelay";
const apiConnector = new APIConnector();

export const getLifts = async (listParameters?: ListQuery): Promise<LiftWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm)
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const url = `lift?include=building.site${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const rows: Lift[] = data.map((lift): Lift => {
                    return {
                        id: lift['id'],
                        name: lift['lift-name'],
                        site: lift['building']?.['site']?.['site-name'],
                        building: lift['building']?.['building-name'],
                        floors: lift['total-floors'],
                        readerName: lift['reader-name'],
                        readerId: lift['reader-id'],
                        ioModules: lift['total-io-devices'],
                        level: lift['floors-start-level']
                    }
                })

                return resolve({ rows, pagination });
            })

        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}

export const getLiftNamesWithPagination = async (listParameters?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filters: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filters);
        const liftFields = "fields[lift]=liftName,building";
        const buildingFields = "fields[building]=buildingName";
        const url = `lift?${paginationString}&include=building&${liftFields}&${buildingFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }

            new Deserializer().deserialize(json, (err: any, lifts: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError })
                }

                const liftNames: Name[] = lifts.map((lift) => {
                    return {
                        value: lift['id'],
                        label: lift['lift-name'],
                        optionalColumns: [{ key: 'building', value: lift['building']?.['building-name'] || '-' }]
                    }
                });

                return resolve({ rows: liftNames, pagination: pagination });
            });
        })
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}

export const getLiftById = async (liftId: string): Promise<Lift> => {
    try {
        const liftFields = "fields[lift]=liftName,liftDesc,floorsTotal,floorsStartLevel,building,liftType,area,accessProfile,totalFloors,totalReaders,totalIoDevices,readerId,readerName";
        const buildingFields = "fields[building]=buildingName,site";
        const liftReaderFields = "fields[deviceImprox]=devName";
        const siteFields = "fields[site]=siteName";
        const liftTypeFields = "fields[liftType]=liftTypeName";
        const url = `lift/${liftId}?include=building.site&${liftFields}&${buildingFields}&${liftReaderFields}&${siteFields}&${liftTypeFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({
                    status: response.status,
                    message: getConstraintViolation(json.errors[0].detail),
                });
            }

            new Deserializer().deserialize(json, (error: any, data: any) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const lift: Lift = {
                    id: data.id,
                    name: data["lift-name"],
                    floors: data['total-floors'],
                    readerName: data['reader-name'],
                    readerId: data['reader-id'],
                    ioModules: data['total-io-devices'],
                    building: data.building?.["building-name"],
                    buildingId: data.building?.id,
                    site: data.building?.site?.["site-name"],
                    siteId: data.building?.site?.id,
                    level: data['floors-start-level'],
                    description: data['lift-desc']
                };

                resolve(lift);
            });
        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
};

export const getLiftReaders = async (listParameters?: ListQuery ): Promise<ReaderWrapper> => {
    try {
        let filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialUrl = createPartialURL(listParameters?.goToPage,listParameters?.pageSize, listParameters?.sortBy,filter);
        const includes = "include=deviceImprox,deviceImprox.unitType,deviceImprox.site,deviceImprox.profile";
        const liftReaderFields = "fields[liftReader]=deviceImprox,lift,controllerName";
        const terminalFields = "fields[terminal]=devName,devFixedAddr,devIpAddress,devEnabled,devCommsStatus,interpretedLicense,unitType,site,profile,controller,location,devSerial,devReaderSerial,hasPendingLicense";
        const unitTypeFields = "fields[unitType]=trnName,utCategory";
        const siteFields = "fields[site]=siteName";
        const profileFields = "fields[profile]=trnName";
        const controllerFields = "fields[controller]=devName,devFixedAddr";
        const url = `liftReader?${includes}&${liftReaderFields}&${terminalFields}&${unitTypeFields}&${siteFields}&${profileFields}&${controllerFields}${partialUrl}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({
                    status: response.status,
                    message: getConstraintViolation(json.errors[0].detail)
                });
            }

            new Deserializer().deserialize(json, (error: any, data: any) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const readers: Reader[] = data.map((liftReader: any): Reader => {
                    const reader = liftReader['device-improx'];
                    const devSerial = reader?.['dev-serial'];
                    const devReaderSerial = reader?.['dev-reader-serial'];

                    return {
                        id: reader?.['id'] || 0,
                        name: reader?.['dev-name'] || '-',
                        fixedAddress: reader?.['dev-fixed-addr'] || '-',
                        ipAddress: reader?.['dev-ip-address'] || '-',
                        site: reader?.['site']?.['site-name'] || '-',
                        preset: reader?.['profile']?.['trn-name'] || '-',
                        presetId: reader?.['profile']?.['id'] || 0,
                        type: reader?.['unit-type']?.['trn-name'] || '-',
                        controller: liftReader?.['controller-name'] || '-',
                        controllerAddress: reader?.['controller']?.['dev-fixed-addr'] || '-',
                        enabled: reader?.['dev-enabled'] || 0,
                        devCommsStatus: reader?.['dev-comms-status'] || 0,
                        status: getDeviceLicenceStatus(true, reader?.['dev-enabled'], reader?.['dev-comms-status']),
                        musteringPoint: false,
                        devLicense: reader?.['interpreted-license'] || (reader?.['has-pending-license'] ? '' : '-'),
                        devReaderSerial: devReaderSerial || devSerial || '-',
                        pendingLicense: reader?.['has-pending-license'] || false
                    };
                });

                return resolve({ rows: readers, pagination });
            });
        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
};

export const getLiftDetails = async (listParameters?: ListQuery): Promise<any> => {
    try {
        let filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialUrl = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const includes = "include=floor.building.site,deviceImprox";
        const liftDetailFields = "fields[liftDetail]=floor,deviceImprox,lift,deviceName,actionName";
        const floorFields = "fields[floor]=floorName,floorLevel,building";
        const buildingFields = "fields[building]=buildingName,site";
        const siteFields = "fields[site]=siteName";
        const deviceFields = "fields[deviceImprox]=devName,devFixedAddr";
        const url = `liftDetail?${includes}&${liftDetailFields}&${floorFields}&${buildingFields}&${siteFields}&${deviceFields}${partialUrl}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({
                    status: response.status,
                    message: getConstraintViolation(json.errors[0].detail)
                });
            }

            new Deserializer().deserialize(json, (error: any, data: any) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const liftDetails = data.map((detail) => {
                    return {
                        id: detail['id'],
                        floorId: detail['floor']?.['id'] || '',
                        deviceImproxId: detail['device-improx']?.['id'] || '',
                        deviceName: detail['device-name'] || '',
                        actionName: detail['action-name'] || '',
                        name: detail['floor']?.['floor-name'] || '-',
                        building: detail['floor']?.['building']?.['building-name'] || '-',
                        site: detail['floor']?.['building']?.['site']?.['site-name'] || '-',
                        level: detail['floor']?.['floor-level']?.toString() || '-',
                    };
                });
                
                return resolve({ rows: liftDetails, pagination });
            });
        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
};

export const getLiftIODevices = async (listParameters?: ListQuery): Promise<IOModuleWrapper> => {
    try {
        let filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialUrl = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const includes = "include=deviceImprox,deviceImprox.unitType,deviceImprox.site,deviceImprox.profile";
        const IoDeviceFields = "fields[liftIoDevice]=deviceImprox,lift,controllerName";
        const terminalFields = "fields[terminal]=devName,devFixedAddr,devIpAddress,devEnabled,devCommsStatus,interpretedLicense,unitType,site,profile,controller,location,devSerial,devReaderSerial,hasPendingLicense";
        const unitTypeFields = "fields[unitType]=trnName,utCategory";
        const siteFields = "fields[site]=siteName";
        const profileFields = "fields[profile]=trnName";
        const controllerFields = "fields[controller]=devName,devFixedAddr";
        const url = `liftIoDevice?${includes}&${IoDeviceFields}&${terminalFields}&${unitTypeFields}&${siteFields}&${profileFields}&${controllerFields}${partialUrl}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({
                    status: response.status,
                    message: getConstraintViolation(json.errors[0].detail)
                });
            }

            new Deserializer().deserialize(json, (error: any, data: any) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const ioDevices: IOModule[] = data.map((liftIoDevice: any): IOModule => {
                    const ioDevice = liftIoDevice['device-improx'];
                    return {
                        id: ioDevice['id'],
                        name: ioDevice['dev-name'] || '-',
                        fixedAddress: ioDevice['dev-fixed-addr'] || '-',
                        ipAddress: ioDevice['wan-ip-address'] || '-',
                        site: ioDevice['site']?.['site-name'] || '-',
                        preset: ioDevice['profile']?.['trn-name'] || '-',
                        enabled: ioDevice['dev-enabled'] || 0,
                        controller: liftIoDevice['controller-name'] || '-',
                        controllerAddress: ioDevice['controller']?.['dev-fixed-addr'] || '-',
                        status: getDeviceLicenceStatus(true, ioDevice['dev-enabled'], ioDevice['dev-comms-status']),
                    };
                });

                return resolve({ rows: ioDevices, pagination });
            });
        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
};

export const updateLift = async (lift: Lift): Promise<any> => {
    try {
        const newLift = {
            data: {
                type: 'lift',
                id: lift.id,
                attributes: {
                    liftName: lift.name,
                    liftDesc: lift.description || '',
                    floorsTotal: lift.floors,
                    floorsStartLevel: lift.level,
                },
                relationships: {
                    building: {
                        data: lift.buildingId ? {
                            type: 'building',
                            id: lift.buildingId
                        } : undefined
                    }
                }
            }
        }

        const url = `lift/${newLift.data.id}`;
        const liftResponse = await apiConnector.sendData(url, false, newLift);

        return new Promise(async (resolve, reject) => {
            if (liftResponse) {
                let settingDataResponses: Response[] = new Array(0);
                return resolve(settingDataResponses)
            }
            else {
                return reject({ status: INTERNAL_SERVER_ERROR, id: lift.id, success: false });
            }
        })
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}

export const assignUnassignLiftEntities = async (toBeAdded: string[], toBeRemoved: string[], liftId: string, entity: "liftIoDevice" | "liftReader") => {
    try {
        //Unassign
        const removed = toBeRemoved.map(async (readerId) => {
            let url = `${entity}?filter=lift.id==${liftId};deviceImprox.id==${readerId}`
            const response = await apiConnector.fetchData(url);
            const json = await response.json();

            if (json.data && json.data.length > 0) {
                const liftReaderId = json.data[0].id;
                const relationship = {
                    data: {
                        type: entity,
                        id: liftReaderId
                    }
                };

                return await apiConnector.deleteData(`${entity}/${liftReaderId}`, relationship);
            }
        });

        //Assign
        const added = toBeAdded.map(async (readerId) => {
            const relationship = {
                data: {
                    type: entity,
                    relationships: {
                        lift: {
                            data: {
                                type: 'lift',
                                id: liftId
                            }
                        },
                        deviceImprox: {
                            data: {
                                type: 'terminal',
                                id: readerId
                            }
                        }
                    }
                }
            };

            return await apiConnector.sendData(entity, true, relationship);
        });

        return Promise.allSettled([...removed, ...added])
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
};

export const saveLift = async (lift: any): Promise<StoreEntityResult> => {
    try {
        const requestBody = {
            type: 'lift',
            attributes: {
                name: lift.name,
                desc: lift.desc,
                floorLevelStart: lift.floorLEvelStart,
                floorLevelEnd: lift.floorLevelEnd,
                autoCreateFloorGroupsPerFloor: lift.autoCreateFloorGroupsPerFloor,
                autoCreateFullAccessFloorGroup: lift.autoCreateFullAccessFloorGroup,
                autoCreateAccessProfile: lift.autoCreateAccessProfile
            },
            relationships: {
                siteId: lift.siteId,
                buildingId: lift.buildingId,
                floors: lift.floorIds.map((id: string) => ({ id })),
                readerId: lift.readerId,
                ioDevices: lift.mapRelays.map((mapRelay: MapRelay, index: number) => {
                    return {
                        id: mapRelay.id,
                        ordinal: index + 1
                    }
                })
            }
        }

        let response: any = await apiConnector.sendData('', true, requestBody, true)
        const json = await response.json();

        return new Promise((resolve, reject) => {

            if (json && json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            if (response) {
                if (response.errors) {
                    return reject({ status: response.status, message: getConstraintViolation(response.errors[0].detail) })
                }

                return resolve({ id: 0, success: true });
            }

            return reject({ status: response.status, message: genericError });

        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}

export const deleteLift = async (id: string): Promise<DeleteEntityResult> => {
    try {
        const url = `lift/${id}`;
        const lift = {
            data: {
                type: "lift",
                id: id
            }
        }
        const response = await apiConnector.deleteData(url, lift)

        return new Promise(async (resolve, reject) => {
            if (response.status === 204) {
                return resolve({ id: id, success: true })
            }

            const json = await response.json();
            return reject({status: response.status, id: id, success: false, message: getConstraintViolation(json.errors[0].detail) });
        })
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}
